import React, { useState } from 'react';
import { keys, map, orderBy } from 'lodash';
import { v4 } from 'uuid';
import TimePickersV2 from '../components/TimePickersV2';
import { Days, getDayTranslation } from '../../../utils';
import ClosingDays from './ClosingDays';
import Switch from '../../../components/common/Switch';

const tabs = {
  hours: 'hours',
  closingDays: 'closingDays'
};

const daysOptions = map(keys(Days), (key) => ({
  key,
  value: getDayTranslation(key)
}));

const getSortedHours = (dayHours) => [
  ...orderBy(
    dayHours.filter((h) => h.start && h.stop),
    ['start']
  ),
  ...dayHours.filter((h) => !h.start && !h.stop)
];

const toMinutes = (time) => {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const getOverlappingUuids = (dayHours) => {
  const conflicting = new Set();
  const filled = dayHours.filter((h) => h.start && h.stop);
  for (let i = 0; i < filled.length; i++) {
    for (let j = i + 1; j < filled.length; j++) {
      const aStart = toMinutes(filled[i].start);
      const aStop = toMinutes(filled[i].stop);
      const bStart = toMinutes(filled[j].start);
      const bStop = toMinutes(filled[j].stop);
      if (aStart < bStop && aStop > bStart) {
        conflicting.add(filled[i].uuid);
        conflicting.add(filled[j].uuid);
      }
    }
  }
  return conflicting;
};

const OpeningHoursAndClosingDays = ({ type, days, onAddDays, onRemoveDays, onChangeDays, hours, onChangeHours, onAddHours, onRemoveHour, context }) => {
  const [activeTab, setActiveTab] = useState(tabs.hours);

  const handleRemoveHour = (id) => {
    onRemoveHour(hours.filter((h) => h.uuid !== id));
  };

  const onHoursChanged = (start, stop, day, id) => {
    const exists = hours.some((h) => h.uuid === id);
    onChangeHours(exists ? hours.map((h) => (h.uuid === id ? { ...h, start, stop } : h)) : [...hours, { uuid: id, start, stop, day, type }]);
  };

  const handleAddHours = (day) => {
    onAddHours([...hours, { uuid: v4(), start: '', stop: '', day, type }]);
  };

  const handleToggleDay = (dayKey, isOpen) => {
    if (isOpen) {
      handleAddHours(dayKey);
    } else {
      onRemoveHour(hours.filter((h) => h.day !== dayKey));
    }
  };

  const onDaysChanged = (updateDay) => {
    const { start, stop, description, uuid } = updateDay;
    const exists = days.some((d) => d.uuid === uuid);
    onChangeDays(exists ? days.map((d) => (d.uuid === uuid ? { ...d, start, stop, description } : d)) : [...days, { uuid, start, stop, description, type }]);
  };

  const handleAddDays = () => {
    onAddDays([
      ...days,
      {
        uuid: v4(),
        start: new Date(),
        stop: new Date(new Date().setDate(new Date().getDate() + 1)),
        type,
        description: 'We are closed.'
      }
    ]);
  };

  const handleRemoveDays = (id) => {
    onRemoveDays(days.filter((d) => d.uuid !== id));
  };

  const renderHours = () => (
    <div className='builder-grid builder-grid-cols-4 builder-gap-4'>
      {daysOptions.map((day) => {
        const selectedHours = hours.filter((h) => h.day === day.key);
        const isOpen = selectedHours.length > 0;
        const orderedHours = getSortedHours(selectedHours);

        const overlapping = getOverlappingUuids(orderedHours);

        return (
          <div key={day.key} className='builder-flex builder-flex-col builder-border builder-border-gray-200 builder-rounded'>
            <div className='builder-flex builder-items-center builder-justify-between builder-px-3 builder-py-2 builder-bg-gray-50 builder-border-b builder-border-gray-200'>
              <span className='builder-text-sm builder-font-medium builder-text-gray-700'>{day.value}</span>
              <Switch isChecked={isOpen} onChange={(checked) => handleToggleDay(day.key, checked)} size='small' label='Open' />
            </div>

            {overlapping.size > 0 && (
              <div className='builder-flex builder-items-center builder-px-3 builder-py-2 builder-bg-orange-50 builder-border-b builder-border-orange-100 builder-text-xs builder-text-orange-600'>
                <i className='fal fa-exclamation-triangle builder-mr-2' />
                Overlapping slots — please review the times below.
              </div>
            )}

            {!isOpen && (
              <div className='builder-flex builder-flex-1 builder-flex-col builder-items-center builder-justify-center builder-p-4'>
                <p className='builder-text-xs builder-text-gray-400 builder-text-center'>
                  This day is closed. Toggle <span className='builder-font-medium'>Open</span> to add opening hours.
                </p>
              </div>
            )}
            {isOpen && (
              <div className='builder-p-3 builder-space-y-2'>
                {orderedHours.map((hour) => (
                  <div key={hour.uuid} className={`builder-flex builder-items-center builder-justify-between builder-rounded builder-px-1 ${overlapping.has(hour.uuid) ? 'builder-bg-orange-50' : ''}`}>
                    <TimePickersV2 onChange={(start, stop) => onHoursChanged(start, stop, hour.day, hour.uuid)} dayKey={hour.day} start={hour.start} stop={hour.stop} />
                    <div className='builder-ml-2 builder-cursor-pointer builder-text-red-400 hover:builder-text-red-800 builder-flex-shrink-0' onClick={() => handleRemoveHour(hour.uuid)}>
                      <i className='fal fa-trash' />
                    </div>
                  </div>
                ))}
                <div className='builder-flex builder-items-center builder-w-fit builder-text-xs builder-text-gray-400 builder-cursor-pointer hover:builder-text-gray-600 builder-pt-1' onClick={() => handleAddHours(day.key)}>
                  <i className='fal fa-plus builder-mr-1' />
                  Add slot
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const tabClass = (tab) =>
    `builder-px-4 builder-pb-3 builder-text-sm builder-font-medium builder-cursor-pointer builder-border-b-2 builder-transition-colors ${
      activeTab === tab ? 'builder-border-blue-600 builder-text-blue-600' : 'builder-border-transparent builder-text-gray-500 hover:builder-text-gray-700'
    }`;

  return (
    <div className='builder-flex builder-flex-col builder-w-full'>
      <div className='builder-flex builder-w-full builder-py-4'>
        <div className='builder-flex builder-flex-col builder-flex-1 builder-w-full'>
          <div className='builder-flex builder-border-b builder-border-gray-200 builder-mb-6'>
            <div className={tabClass(tabs.hours)} onClick={() => setActiveTab(tabs.hours)}>
              Opening hours
            </div>
            <div className={tabClass(tabs.closingDays)} onClick={() => setActiveTab(tabs.closingDays)}>
              Closing days
            </div>
          </div>
          <div>
            {activeTab === tabs.hours && renderHours()}
            {activeTab === tabs.closingDays && <ClosingDays days={days} onChange={onDaysChanged} onRemove={handleRemoveDays} onAdd={handleAddDays} context={context} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningHoursAndClosingDays;
